import type { LeaderDevelopmentMemory } from "@/lib/behavior/behaviorContracts";

type InterventionContinuityRow = {
  recordId: string;
  createdAt: string | null;
  source: "typed" | "legacy";
  leaderDevelopment: LeaderDevelopmentMemory;
};

export type InterventionMemoryRecord = {
  recordId: string;
  createdAt: string | null;
  focus: string | null;
  nextSignal: string | null;
  source: "typed" | "legacy";
};

export type InterventionMemory = {
  raisedPreviously: InterventionMemoryRecord;
  latestEvidence: InterventionMemoryRecord;
  changedSinceRaised: boolean;
} | null;

function normalizeLine(value: string | null | undefined) {
  return value?.replace(/\s+/g, " ").trim() || null;
}

function toMemoryRecord(row: InterventionContinuityRow): InterventionMemoryRecord {
  return {
    recordId: row.recordId,
    createdAt: row.createdAt,
    focus: normalizeLine(row.leaderDevelopment.discussed[0] || row.leaderDevelopment.nextFocus[0] || null),
    nextSignal: normalizeLine(row.leaderDevelopment.nextSession[0] || null),
    source: row.source,
  };
}

function sameMeaning(a: string | null, b: string | null) {
  return normalizeLine(a)?.toLowerCase() === normalizeLine(b)?.toLowerCase();
}

export function buildInterventionMemory(params: {
  continuityRows: InterventionContinuityRow[];
}): InterventionMemory {
  const rows = params.continuityRows || [];
  if (rows.length < 2) return null;

  const latestEvidence = toMemoryRecord(rows[0]);
  const raisedPreviously = toMemoryRecord(rows[1]);

  return {
    raisedPreviously,
    latestEvidence,
    changedSinceRaised:
      !sameMeaning(raisedPreviously.focus, latestEvidence.focus)
      || !sameMeaning(raisedPreviously.nextSignal, latestEvidence.nextSignal),
  };
}
